/** Sprint 5-b PWA · 會議室 tab · 近期會議列表（event_type=meeting·點入進會議室）。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import DescriptionIcon from '@mui/icons-material/Description';

import { useMessage } from '../../../contexts/MessageContext';
import { formatLocal } from '../../../utils/dateHelper';
import { getEvents } from '../../../services/eeMyCalendarService';
import { EeMyCalendar } from '../../../types/eeMyCalendar';

const UpcomingMeetingsPage: React.FC = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pwaMeetings.${k}`, d);
  const { showApiError } = useMessage();
  const [items, setItems] = useState<EeMyCalendar[]>([]);

  const load = useCallback(async () => {
    const now = new Date();
    const start = new Date(now.getTime() - 864e5);       // 含今日稍早
    const end = new Date(now.getTime() + 30 * 864e5);
    try {
      const list = await getEvents({ start: start.toISOString(), end: end.toISOString(), expand: true });
      setItems(list
        .filter((e) => e.event_type === 'meeting')
        .sort((a, b) => (a.dtstart || '').localeCompare(b.dtstart || '')));
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', my: 1 }}>
        <Typography variant="h6">🧑‍🤝‍🧑 {tt('title', '會議室')}</Typography>
        {/* item 7：/ee_pwa_minutes 已改為公司會議紀錄·個人語音會議歷史改走行事曆內入口 */}
        <Button size="small" startIcon={<DescriptionIcon />} onClick={() => nav('/ee_pwa_meetings/history')}>
          {tt('minutesEntry', '會議紀錄')}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary">{tt('upcoming', '近期會議')}</Typography>

      <Stack spacing={1} sx={{ mt: 1 }}>
        {items.length === 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {tt('empty', '近期沒有會議。用「語音建」建立會議事件後即可進會議室錄音。')}</Typography>
          </Paper>
        )}
        {items.map((e) => (
          <Paper key={`${e.id}-${e.dtstart}`} variant="outlined" sx={{ p: 1.5 }}
            onClick={() => nav(`/ee_pwa_meetings/${e.id}`)}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <GroupsIcon color="primary" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" noWrap>{e.summary}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {e.dtstart ? formatLocal(e.dtstart) : ''}{e.location ? ` · ${e.location}` : ''}</Typography>
              </Box>
              <Chip size="small" color="primary" label={tt('enter', '進入')} />
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default UpcomingMeetingsPage;
